/**
 * Image mapping system using ES module imports for production compatibility
 * Maps JSON paths to imported asset URLs that work in both dev and production
 */

// Import all images as ES modules
import airtechLogo from "@/assets/images/airtechlogo.png";
import aeroEngine from "@/assets/images/aero.jpg";
import quicksilverAircraft from "@/assets/images/quicksilver-aircraft.jpg";
import sunNFunBanner from "@/assets/images/sun-n-fun-banner.jpg";

// Featured product images
import tankFuel10Gal from "@/assets/images/tank-fuel-10-gal.jpg";
import batteryBoxSport from "@/assets/images/battery-box-sport.jpg";
import batteryBoxTm from "@/assets/images/battery-box-tm.jpg";
import brakeWheelsKit from "@/assets/images/brake-wheels-kit.jpg";
import clutchAssembled from "@/assets/images/clutch-assembled.jpg";
import tensionTesterBelt from "@/assets/images/tension-tester-belt.jpg";
import trimKit from "@/assets/images/trim-kit.jpg";
import tubeBoomUpWide from "@/assets/images/tube-boom-up-wide.jpg";
import colorPicker from "@/assets/images/colorpicker.gif";

/**
 * Image mapping from JSON paths to imported asset URLs
 * This ensures proper asset handling in both development and production builds
 */
const imageMap: Record<string, string> = {
  // Logo images
  "/wp-content/uploads/2025/01/airtechlogo.png": airtechLogo,
  "/assets/images/airtechlogo.png": airtechLogo,
  "/images/airtechlogo.png": airtechLogo,
  "airtechlogo.png": airtechLogo,
  
  // Hero section images
  "/wp-content/uploads/2025/07/aero.jpg": aeroEngine,
  "/assets/images/aero.jpg": aeroEngine,
  "/images/aero.jpg": aeroEngine,
  "aero.jpg": aeroEngine,
  
  "/wp-content/uploads/2025/09/Quicksilver-Spri-1024x683.jpg": quicksilverAircraft,
  "/assets/images/quicksilver-aircraft.jpg": quicksilverAircraft,
  "/images/quicksilver-aircraft.jpg": quicksilverAircraft,
  "quicksilver-aircraft.jpg": quicksilverAircraft,
  
  "/wp-content/uploads/2026/02/sun-n-fun2026-1024x373.jpg": sunNFunBanner,
  "/assets/images/sun-n-fun-banner.jpg": sunNFunBanner,
  "/images/sun-n-fun-banner.jpg": sunNFunBanner,
  "sun-n-fun-banner.jpg": sunNFunBanner,
  
  // Featured product images
  "/wp-content/uploads/2021/02/rt1_1-1.jpg": tankFuel10Gal,
  "/assets/images/tank-fuel-10-gal.jpg": tankFuel10Gal,
  "/images/tank-fuel-10-gal.jpg": tankFuel10Gal,
  "tank-fuel-10-gal.jpg": tankFuel10Gal,
  
  "/wp-content/uploads/2024/02/IMG_1907-400x400.jpg": batteryBoxSport,
  "/assets/images/battery-box-sport.jpg": batteryBoxSport,
  "/images/battery-box-sport.jpg": batteryBoxSport,
  "battery-box-sport.jpg": batteryBoxSport,
  
  "/wp-content/uploads/2021/02/IMG_2055-400x400.jpg": batteryBoxTm,
  "/wp-content/uploads/2021/02/IMG_2055.jpg": batteryBoxTm,
  "/assets/images/battery-box-tm.jpg": batteryBoxTm,
  "/images/battery-box-tm.jpg": batteryBoxTm,
  "battery-box-tm.jpg": batteryBoxTm,
  
  "/wp-content/uploads/2021/02/BR160-H.jpg": brakeWheelsKit,
  "/assets/images/brake-wheels-kit.jpg": brakeWheelsKit,
  "/images/brake-wheels-kit.jpg": brakeWheelsKit,
  "brake-wheels-kit.jpg": brakeWheelsKit,
  
  "/wp-content/uploads/2021/02/RK400c220.jpg": clutchAssembled,
  "/assets/images/clutch-assembled.jpg": clutchAssembled,
  "/images/clutch-assembled.jpg": clutchAssembled,
  "clutch-assembled.jpg": clutchAssembled,
  
  "/wp-content/uploads/2021/02/Q60033220.jpg": tensionTesterBelt,
  "/assets/images/tension-tester-belt.jpg": tensionTesterBelt,
  "/images/tension-tester-belt.jpg": tensionTesterBelt,
  "tension-tester-belt.jpg": tensionTesterBelt,
  
  "/wp-content/uploads/2021/04/AT501_220-1-400x400.jpg": trimKit,
  "/assets/images/trim-kit.jpg": trimKit,
  "/images/trim-kit.jpg": trimKit,
  "trim-kit.jpg": trimKit,
  
  "/wp-content/uploads/2021/02/Q40179W_220.jpg": tubeBoomUpWide,
  "/assets/images/tube-boom-up-wide.jpg": tubeBoomUpWide,
  "/images/tube-boom-up-wide.jpg": tubeBoomUpWide,
  "tube-boom-up-wide.jpg": tubeBoomUpWide,
  
  // Footer color picker
  "/wp-content/uploads/2024/06/colorpicker.gif": colorPicker,
  "/assets/images/colorpicker.gif": colorPicker,
  "/images/colorpicker.gif": colorPicker,
  "colorpicker.gif": colorPicker,
};

/**
 * Resolves image paths from JSON data to actual imported asset URLs
 * @param imagePath - Path from JSON data (e.g., "/assets/images/logo.png")
 * @returns Resolved asset URL that works in both dev and production
 */
export function resolveImagePath(imagePath: string): string {
  // First try exact match in image map
  if (imageMap[imagePath]) {
    return imageMap[imagePath];
  }

  // Try normalized paths (with and without /assets prefix)
  const normalizedPath = imagePath.startsWith("/assets/")
    ? imagePath
    : `/assets${imagePath.startsWith("/") ? imagePath : "/" + imagePath}`;

  if (imageMap[normalizedPath]) {
    return imageMap[normalizedPath];
  }

  // Try legacy path format
  const legacyPath = imagePath.replace("/assets", "");
  if (imageMap[legacyPath]) {
    return imageMap[legacyPath];
  }

  // Fallback: log warning and return original path
  console.warn(`Image not found in image map: ${imagePath}. Available images:`, Object.keys(imageMap));
  return imagePath;
}

/**
 * Get all available image paths for debugging
 */
export function getAvailableImages(): string[] {
  return Object.keys(imageMap);
}

/**
 * Legacy export for compatibility
 */
export const resolveImageUrl = resolveImagePath;
